import { Node, Position } from 'reactflow';

// Returns the position (top, right, bottom, left) closest to the intersection point
function getEdgePosition(node: Node, intersectionPoint: { x: number; y: number }) {
  const n = { ...node.positionAbsolute, ...node };
  const nx = Math.round(n.x!);
  const ny = Math.round(n.y!);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  const width = node.width ?? 220;
  const _height = node.height ?? 100;

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + width - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  return Position.Bottom;
}

// Returns the parameters needed to create an edge
function getNodeIntersection(intersectionNode: Node, targetNode: Node) {
  const intersectionNodeWidth = intersectionNode.width ?? 220;
  const intersectionNodeHeight = intersectionNode.height ?? 100;
  const intersectionNodePosition = intersectionNode.positionAbsolute ?? intersectionNode.position;
  const targetPosition = targetNode.positionAbsolute ?? targetNode.position;

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = (targetPosition.x ?? 0) + (targetNode.width ?? 220) / 2;
  const y1 = (targetPosition.y ?? 0) + (targetNode.height ?? 100) / 2;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1));
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

// Returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) for a floating edge
export function getEdgeParams(source: Node, target: Node) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}
